import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cls from "./UserLoginInfo.module.scss";
import api from "@/utils/axios";
import { timeAgo } from "@/utils/timeAgo";

const ROLES = ["admin", "editor", "member"];
const STATUSES = ["active", "suspended", "banned", "deleted"];

const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

const UserLoginInfo = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [avatar, setAvatar] = useState(null);
  const [form, setForm] = useState({
    username: "",
    email: "",
    birthday: "",
    role: "",
    status: "",
    isRoot: false,
    password: "",
  });
  const [meta, setMeta] = useState({
    emailVerifiedAt: null,
    lastLoginAt: null,
    failedLoginCount: 0,
    twoFactorEnabled: false,
    lastPasswordChangeAt: null,
    tosAcceptedAt: null,
    privacyAcceptedAt: null,
    createdAt: null,
    updatedAt: null,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get(
        `/v1/admin/members/single/login-info/${userId}`
      );

      const { user, avatar: av } = res.data || {};
      if (!user) {
        setError("User not found.");
        setLoading(false);
        return;
      }

      setAvatar(av || null);

      setForm({
        username: user.username || "",
        email: user.email || "",
        birthday: toDateInput(user.birthday),
        role: user.role || "",
        status: user.status || "",
        isRoot: Boolean(user.isRoot),
        password: "",
      });

      setMeta({
        emailVerifiedAt: user.emailVerifiedAt || null,
        lastLoginAt: user.lastLoginAt || null,
        failedLoginCount:
          typeof user.failedLoginCount === "number" ? user.failedLoginCount : 0,
        twoFactorEnabled: Boolean(user.twoFactorEnabled),
        lastPasswordChangeAt: user.lastPasswordChangeAt || null,
        tosAcceptedAt: user.tosAcceptedAt || null,
        privacyAcceptedAt: user.privacyAcceptedAt || null,
        createdAt: user.createdAt || null,
        updatedAt: user.updatedAt || null,
      });
    } catch (err) {
      console.error("Error loading login info:", err);
      setError("Failed to load login info.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleChange = (field) => (e) => {
    const value = field === "isRoot" ? e.target.checked : e.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSaveMessage("");

    try {
      const payload = {
        username: form.username.trim(),
        email: form.email.trim(),
        birthday: form.birthday || null,
        role: form.role,
        status: form.status,
        isRoot: form.isRoot,
      };

      if (form.password && form.password.trim().length > 0) {
        payload.password = form.password.trim();
      }

      const res = await api.patch(
        `/v1/admin/members/single/login-info/${userId}`,
        payload
      );

      const updated = res.data.user;
      if (updated) {
        setForm((prev) => ({
          ...prev,
          username: updated.username || "",
          email: updated.email || "",
          birthday: toDateInput(updated.birthday),
          role: updated.role || "",
          status: updated.status || "",
          isRoot: Boolean(updated.isRoot),
          password: "",
        }));

        setMeta((prev) => ({
          ...prev,
          emailVerifiedAt: updated.emailVerifiedAt || null,
          lastLoginAt: updated.lastLoginAt || prev.lastLoginAt,
          failedLoginCount:
            typeof updated.failedLoginCount === "number"
              ? updated.failedLoginCount
              : prev.failedLoginCount,
          twoFactorEnabled: Boolean(updated.twoFactorEnabled),
          lastPasswordChangeAt:
            updated.lastPasswordChangeAt || prev.lastPasswordChangeAt,
          tosAcceptedAt: updated.tosAcceptedAt || prev.tosAcceptedAt,
          privacyAcceptedAt:
            updated.privacyAcceptedAt || prev.privacyAcceptedAt,
          createdAt: updated.createdAt || prev.createdAt,
          updatedAt: updated.updatedAt || prev.updatedAt,
        }));
      }

      setSaveMessage("Login info updated successfully.");
    } catch (err) {
      console.error("Error updating login info:", err);
      const msg =
        err?.response?.data?.message || "Failed to update login info.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button
            type="button"
            className={cls.backBtn}
            onClick={() => navigate(`/admin/members/single/${userId}`)}
          >
            ← Back to Members
          </button>
        </div>
        <div className={cls.loadingCard}>
          <span className={cls.spinner} />
          <span>Loading login info...</span>
        </div>
      </div>
    );
  }

  if (error && !form.username) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button
            type="button"
            className={cls.backBtn}
            onClick={() => navigate(`/admin/members/single/${userId}`)}
          >
            ← Back to Members
          </button>
        </div>
        <div className={cls.errorCard}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.headerRow}>
        <button
          type="button"
          className={cls.backBtn}
          onClick={() => navigate(`/admin/members/single/${userId}`)}
        >
          ← Back to @{form.username}
        </button>

        <div className={cls.titleBlock}>
          <h1 className={cls.title}>User Login & Identity</h1>
          <p className={cls.subtitle}>
            Manage core login details, account status, and system flags for this
            user.
          </p>
        </div>
      </div>

      <div className={cls.heroRow}>
        <div className={cls.avatarBlock}>
          {avatar?.url ? (
            <div className={cls.avatarWrap}>
              <img
                src={avatar.url}
                alt="Member avatar"
                className={cls.avatarImg}
              />
            </div>
          ) : (
            <div className={`${cls.avatarWrap} ${cls.avatarFallback}`}>
              <span className={cls.avatarInitial}>
                {form.username ? form.username.charAt(0).toUpperCase() : "?"}
              </span>
            </div>
          )}

          <div className={cls.heroText}>
            <div className={cls.heroUsername}>@{form.username}</div>
            <div className={cls.heroEmail}>{form.email}</div>
          </div>
        </div>

        <div className={cls.heroMeta}>
          <span className={cls.heroChip}>
            Role: <strong>{form.role || "—"}</strong>
          </span>
          <span className={cls.heroChip}>
            Status: <strong>{form.status || "—"}</strong>
          </span>
          <span className={cls.heroChipSoft}>
            Created: {meta.createdAt ? timeAgo(meta.createdAt) : "unknown"}
          </span>
        </div>
      </div>

      <div className={cls.mainGrid}>
        {/* LEFT: Editable form */}
        <section className={cls.card}>
          <h2 className={cls.cardTitle}>Login Profile</h2>
          <form className={cls.form} onSubmit={handleSubmit}>
            <div className={cls.fieldGroup}>
              <label className={cls.label} htmlFor="username">
                Username
              </label>
              <input
                id="username"
                className={cls.input}
                type="text"
                value={form.username}
                onChange={handleChange("username")}
              />
              <p className={cls.helpText}>
                Must be unique. Admin can change this even after signup.
              </p>
            </div>

            <div className={cls.fieldGroup}>
              <label className={cls.label} htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className={cls.input}
                type="email"
                value={form.email}
                onChange={handleChange("email")}
              />
              <p className={cls.helpText}>
                Admin can change email. New value is treated as verified
                immediately.
              </p>
            </div>

            <div className={cls.fieldGroup}>
              <label className={cls.label} htmlFor="password">
                New Password (optional)
              </label>
              <input
                id="password"
                className={cls.input}
                type="password"
                value={form.password}
                onChange={handleChange("password")}
                placeholder="Leave blank to keep current password"
              />
              <p className={cls.helpText}>
                Admin can set a new password without the old one.
              </p>
            </div>

            <div className={cls.fieldRow}>
              <div className={cls.fieldGroup}>
                <label className={cls.label} htmlFor="birthday">
                  Birthday
                </label>
                <input
                  id="birthday"
                  className={cls.input}
                  type="date"
                  value={form.birthday}
                  onChange={handleChange("birthday")}
                />
              </div>

              <div className={cls.fieldGroup}>
                <label className={cls.label} htmlFor="role">
                  Role
                </label>
                <select
                  id="role"
                  className={cls.select}
                  value={form.role}
                  onChange={handleChange("role")}
                >
                  <option value="">Select role</option>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={cls.fieldRow}>
              <div className={cls.fieldGroup}>
                <label className={cls.label} htmlFor="status">
                  Status
                </label>
                <select
                  id="status"
                  className={cls.select}
                  value={form.status}
                  onChange={handleChange("status")}
                >
                  <option value="">Select status</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.fieldGroupCheckbox}>
                <label className={cls.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.isRoot}
                    onChange={handleChange("isRoot")}
                  />
                  <span>Root user</span>
                </label>
                <p className={cls.helpText}>
                  Root users may have special privileges in the system.
                </p>
              </div>
            </div>

            {error && <div className={cls.errorBanner}>{error}</div>}

            {saveMessage && (
              <div className={cls.successBanner}>{saveMessage}</div>
            )}

            <div className={cls.actionsRow}>
              <button type="submit" className={cls.saveBtn} disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </section>

        {/* RIGHT: Meta info */}
        <section className={cls.card}>
          <h2 className={cls.cardTitle}>Login & Security Timeline</h2>
          <div className={cls.metaList}>
            <div className={cls.metaItem}>
              <span className={cls.metaLabel}>Email Verified</span>
              <span className={cls.metaValue}>
                {meta.emailVerifiedAt
                  ? `${formatDate(meta.emailVerifiedAt)} (${timeAgo(
                      meta.emailVerifiedAt
                    )})`
                  : "Not verified"}
              </span>
            </div>

            <div className={cls.metaItem}>
              <span className={cls.metaLabel}>Last Login</span>
              <span className={cls.metaValue}>
                {meta.lastLoginAt
                  ? `${formatDate(meta.lastLoginAt)} (${timeAgo(
                      meta.lastLoginAt
                    )})`
                  : "No login recorded"}
              </span>
            </div>

            <div className={cls.metaItem}>
              <span className={cls.metaLabel}>Failed Logins</span>
              <span className={cls.metaValue}>{meta.failedLoginCount}</span>
            </div>

            <div className={cls.metaItem}>
              <span className={cls.metaLabel}>Two-Factor</span>
              <span className={cls.metaValue}>
                {meta.twoFactorEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>

            <div className={cls.metaItem}>
              <span className={cls.metaLabel}>Last Password Change</span>
              <span className={cls.metaValue}>
                {meta.lastPasswordChangeAt
                  ? `${formatDate(meta.lastPasswordChangeAt)} (${timeAgo(
                      meta.lastPasswordChangeAt
                    )})`
                  : "Never changed"}
              </span>
            </div>

            <div className={cls.metaItem}>
              <span className={cls.metaLabel}>TOS Accepted</span>
              <span className={cls.metaValue}>
                {meta.tosAcceptedAt
                  ? formatDate(meta.tosAcceptedAt)
                  : "Not recorded"}
              </span>
            </div>

            <div className={cls.metaItem}>
              <span className={cls.metaLabel}>Privacy Policy Accepted</span>
              <span className={cls.metaValue}>
                {meta.privacyAcceptedAt
                  ? formatDate(meta.privacyAcceptedAt)
                  : "Not recorded"}
              </span>
            </div>

            <div className={cls.metaItem}>
              <span className={cls.metaLabel}>Created</span>
              <span className={cls.metaValue}>
                {meta.createdAt ? formatDate(meta.createdAt) : "—"}
              </span>
            </div>

            <div className={cls.metaItem}>
              <span className={cls.metaLabel}>Updated</span>
              <span className={cls.metaValue}>
                {meta.updatedAt ? formatDate(meta.updatedAt) : "—"}
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default UserLoginInfo;
